/**
 * MeetingPrep — briefing panel shown above the editor on a meeting instance.
 *
 * Surfaces context that's already in Dossier's graph but hard to assemble manually:
 *  - The previous instance's title + a short snippet of its notes
 *  - Open tasks carrying forward from prior meetings in the same series
 *  - Open tasks currently assigned to each attendee
 *
 * Hidden when there's nothing to show.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, History, ListChecks, ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';
import { apiGet } from '../lib/api';

export interface PrepTask {
  id: string;
  title: string;
  status: string;
  priority: string;
  due_date: string | null;
  assignee_id: string | null;
  assignee_name: string | null;
  source_meeting_id: string | null;
  source_meeting_date?: string;
}

export interface PrepPreviousMeeting {
  id: string;
  series_id: string | null;
  title: string | null;
  meeting_date: string;
  snippet: string | null;
}

export interface PrepAttendeeGroup {
  person_id: string;
  person_name: string;
  open_tasks: PrepTask[];
}

export interface MeetingPrepData {
  instance_id: string;
  previous: PrepPreviousMeeting | null;
  carry_forward_tasks: PrepTask[];
  attendee_tasks: PrepAttendeeGroup[];
}

interface Props {
  instanceId: string;
  className?: string;
}

const PRIORITY_BADGE: Record<string, string> = {
  urgent: 'bg-danger/20 text-danger',
  high: 'bg-warning/20 text-warning',
  medium: 'bg-bg-input text-text-secondary',
  low: 'bg-bg-input text-text-muted',
};

export function MeetingPrep({ instanceId, className = '' }: Props) {
  const [data, setData] = useState<MeetingPrepData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiGet<MeetingPrepData>(`/meeting-instances/${encodeURIComponent(instanceId)}/prep`)
      .then(d => { if (!cancelled) setData(d); })
      .catch(err => { if (!cancelled) setError(err.message || 'Failed to load prep'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [instanceId]);

  if (loading) return null;

  if (error) {
    return (
      <div className={`text-xs text-text-muted ${className}`} role="status">
        Couldn't load meeting prep
      </div>
    );
  }

  if (!data) return null;

  const carryForward = Array.isArray(data.carry_forward_tasks) ? data.carry_forward_tasks : [];
  const attendeeGroups = Array.isArray(data.attendee_tasks) ? data.attendee_tasks : [];
  const hasPrevious = !!data.previous;
  const hasCarry = carryForward.length > 0;
  const hasAttendeeTasks = attendeeGroups.length > 0;

  if (!hasPrevious && !hasCarry && !hasAttendeeTasks) return null;

  return (
    <section
      className={`bg-bg-surface border border-border rounded-lg overflow-hidden ${className}`}
      aria-label="Meeting prep"
    >
      <button
        onClick={() => setCollapsed(c => !c)}
        className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-bg-hover transition-colors"
      >
        <span className="flex items-center gap-2 text-xs uppercase tracking-wider text-text-secondary font-semibold">
          <Sparkles size={12} className="text-accent" />
          Prep
        </span>
        {collapsed ? <ChevronRight size={14} className="text-text-muted" /> : <ChevronDown size={14} className="text-text-muted" />}
      </button>

      {!collapsed && (
        <div className="px-3 pb-3 space-y-4">
          {data.previous && <PreviousSection previous={data.previous} />}
          {hasCarry && <CarryForwardSection tasks={carryForward} />}
          {hasAttendeeTasks && <AttendeeTasksSection groups={attendeeGroups} />}
        </div>
      )}
    </section>
  );
}

function PreviousSection({ previous }: { previous: PrepPreviousMeeting }) {
  const url = previous.series_id
    ? `/meetings/${previous.series_id}/${previous.id}`
    : `/meetings/standalone/${previous.id}`;

  return (
    <div>
      <header className="flex items-center gap-1.5 text-[11px] font-semibold text-text-secondary mb-1.5 uppercase tracking-wider">
        <History size={12} />
        <span>Last time</span>
        <span className="text-text-muted normal-case font-normal">
          · {formatDateLabel(previous.meeting_date)}
        </span>
      </header>
      <Link
        to={url}
        className="block px-2 py-2 rounded-md hover:bg-bg-hover transition-colors group"
      >
        <div className="text-sm text-text-primary group-hover:text-accent truncate">
          {previous.title || formatDateLabel(previous.meeting_date)}
        </div>
        {previous.snippet && (
          <p className="text-xs text-text-muted mt-0.5 line-clamp-3">{previous.snippet}</p>
        )}
      </Link>
    </div>
  );
}

function CarryForwardSection({ tasks }: { tasks: PrepTask[] }) {
  return (
    <div>
      <header className="flex items-center gap-1.5 text-[11px] font-semibold text-text-secondary mb-1.5 uppercase tracking-wider">
        <AlertTriangle size={12} className="text-warning" />
        <span>Carry-forward</span>
        <span className="text-text-muted normal-case font-normal">· {tasks.length}</span>
      </header>
      <ul className="space-y-1">
        {tasks.map(t => <TaskRow key={t.id} task={t} showAssignee />)}
      </ul>
    </div>
  );
}

function AttendeeTasksSection({ groups }: { groups: PrepAttendeeGroup[] }) {
  return (
    <div>
      <header className="flex items-center gap-1.5 text-[11px] font-semibold text-text-secondary mb-1.5 uppercase tracking-wider">
        <ListChecks size={12} />
        <span>Open tasks per attendee</span>
      </header>
      <div className="space-y-3">
        {groups.map(g => (
          <div key={g.person_id}>
            <div className="text-xs font-medium text-text-primary mb-1">
              {g.person_name}
              <span className="text-text-muted ml-1.5 font-normal">({g.open_tasks.length})</span>
            </div>
            <ul className="space-y-1">
              {g.open_tasks.map(t => <TaskRow key={t.id} task={t} />)}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

function TaskRow({ task, showAssignee = false }: { task: PrepTask; showAssignee?: boolean }) {
  const badgeClass = PRIORITY_BADGE[task.priority] ?? PRIORITY_BADGE.medium;
  return (
    <li>
      <Link
        to={`/tasks/${task.id}`}
        className="flex items-start gap-2 px-2 py-1.5 rounded-md hover:bg-bg-hover transition-colors group text-sm"
      >
        <span className="flex-1 min-w-0 truncate text-text-primary group-hover:text-accent">
          {task.title}
        </span>
        {showAssignee && task.assignee_name && (
          <span className="flex-shrink-0 text-[10px] text-text-muted">{task.assignee_name}</span>
        )}
        {task.due_date && (
          <span className="flex-shrink-0 text-[10px] text-text-muted">{formatDateLabel(task.due_date)}</span>
        )}
        <span className={`flex-shrink-0 text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider ${badgeClass}`}>
          {task.priority}
        </span>
      </Link>
    </li>
  );
}

function formatDateLabel(d: string): string {
  // Strip any time component, render short date
  const dateOnly = d.split('T')[0];
  const date = new Date(dateOnly + 'T00:00:00');
  if (isNaN(date.getTime())) return d;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
